import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { getIn, useFormikContext } from 'formik';
import noop from 'lodash';

import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import ArrayFieldFormik from '~/components/core/Formik/NestedFieldFormik/ArrayFieldFormik';
import PlusIcon from '~/components/icons/PlusIcon';

import { useCurrencyFormatter } from '../CurrencyFormatterContext';
import { useExposureSubReserves } from '../hooks/useSubReserves';
import TextFieldFormik, { MonetaryValueTextFieldFormik } from '../TextFieldFormik';

import AmountFieldWithCurrencyDisplay from './AmountFieldWithCurrencyDisplay';

import { useStyles } from '../../assets/styles';
import styles from './SubReservesFormik.module.scss';

const SubReservesFormik = ({
  subReservesFieldId,
  totalFieldId,
  subReservesConfigMap,
  showOnly,
  isDisabled,
  totalFieldLabel,
  children,
  PendingMessage = noop,
  displayNameKey,
}) => {
  const { classes } = useStyles();
  const { values, setFieldValue, isSubmitting } = useFormikContext();

  useEffect(() => {
    const subReservesValue = getIn(values, subReservesFieldId);
    if (subReservesValue) {
      const sumOfSubReserves = Object.keys(subReservesValue).reduce(
        (total, currSubReserve) => total + (subReservesValue[currSubReserve] || 0),
        0
      );
      setFieldValue(totalFieldId, sumOfSubReserves);
    }
  }, [setFieldValue, totalFieldId, values, subReservesFieldId]);

  return (
    <div>
      <div className="mb-24">
        <MonetaryValueTextFieldFormik
          id={totalFieldId}
          label={totalFieldLabel}
          className={classes.textField}
          showOnly={true}
          disabled={isDisabled}
        />
      </div>
      {children && <div>{children}</div>}
      {PendingMessage && (
        <div>
          <PendingMessage />
        </div>
      )}
      <div className="grid grid-cols-2 gap-16">
        {[...subReservesConfigMap.entries()].map(([subReserveKey, subReserveValue]) => (
          <div key={subReserveKey}>
            <MonetaryValueTextFieldFormik
              id={`${subReservesFieldId}.${subReserveKey}`}
              label={subReserveValue[displayNameKey]}
              className={classes.textField}
              fullWidth
              disabled={isSubmitting || isDisabled}
              showOnly={showOnly}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

SubReservesFormik.propTypes = {
  subReservesFieldId: PropTypes.string.isRequired,
  totalFieldId: PropTypes.string.isRequired,
  subReservesConfigMap: PropTypes.object.isRequired,
  showOnly: PropTypes.bool,
  isDisabled: PropTypes.bool,
  totalFieldLabel: PropTypes.string.isRequired,
  children: PropTypes.node,
  PendingMessage: PropTypes.node,
  fullWidth: PropTypes.bool,
  displayNameKey: PropTypes.string.isRequired,
};

const SubReservesAmountFormik = ({ id, showOnly, label, exposure, payableType, children, PendingMessage = noop }) => {
  const { subReservesConfigMap } = useExposureSubReserves(payableType, exposure);

  return (
    <Grid container className={styles.subReserves}>
      <Grid item xs={12}>
        <SubReservesFormik
          subReservesFieldId="sub_reserves"
          totalFieldId={id}
          totalFieldLabel={label}
          subReservesConfigMap={subReservesConfigMap}
          showOnly={showOnly}
          PendingMessage={PendingMessage}
          displayNameKey="desc"
        >
          {children}
        </SubReservesFormik>
      </Grid>
    </Grid>
  );
};

SubReservesAmountFormik.propTypes = {
  id: PropTypes.string.isRequired,
  showOnly: PropTypes.bool,
  label: PropTypes.string.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  children: PropTypes.node,
  PendingMessage: PropTypes.node,
  exposure: PropTypes.object,
  payableType: PropTypes.string.isRequired,
};

const NEW_FIELD_INITIAL_VALUES = {
  type_key: '',
  amount: '',
};

const SingleSubReserveSelectFormik = ({
  id,
  label,
  isSubmitting,
  showOnly,
  subReservesConfigMap,
  payableWithReserve,
}) => {
  const { classes } = useStyles();
  const { currencyFormatter } = useCurrencyFormatter();
  const { values } = useFormikContext();

  const currentSelectValue = getIn(values, id);

  const menuItems = [...subReservesConfigMap.entries()].filter(([subReserveKey]) => {
    return (
      currentSelectValue === subReserveKey ||
      !values.sub_reserve_amounts?.some((sub_reserve_amount) => sub_reserve_amount.type_key === subReserveKey)
    );
  });

  return (
    <TextFieldFormik
      id={id}
      label={label}
      className={classes.textField}
      fullWidth
      select
      disabled={isSubmitting}
      showOnly={showOnly}
    >
      {menuItems.map(([subReserveKey, subReserveValue]) => (
        <MenuItem key={subReserveKey} value={subReserveKey}>
          {subReserveValue.desc} ({currencyFormatter.format(payableWithReserve.sub_reserves?.[subReserveKey] || 0)})
        </MenuItem>
      ))}
    </TextFieldFormik>
  );
};

SingleSubReserveSelectFormik.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  isSubmitting: PropTypes.bool,
  showOnly: PropTypes.bool,
  subReservesConfigMap: PropTypes.array,
  payableWithReserve: PropTypes.object,
};

const MultipleSubReservePaymentSelectFormik = ({
  showOnly,
  payableType,
  exposure,
  payableWithReserve,
  isSubmitting = false,
  requestCurrency,
  conversionRate,
  policyCurrency,
}) => {
  const { classes } = useStyles();
  const { setFieldValue, values } = useFormikContext();

  const { subReservesConfigMap, isMultipleSubReservePaymentsEnabled } = useExposureSubReserves(payableType, exposure);

  const shouldAllowToAddMoreSubReserve =
    isMultipleSubReservePaymentsEnabled &&
    !showOnly &&
    (values.sub_reserve_amounts?.length || 0) < subReservesConfigMap?.size;

  useEffect(() => {
    if (Array.isArray(values.sub_reserve_amounts)) {
      const amount = values.sub_reserve_amounts.reduce((accumulator, currentObject) => {
        return accumulator + currentObject.amount;
      }, 0);
      setFieldValue('amount', amount);
    }
  }, [setFieldValue, values.sub_reserve_amounts]);

  const SUB_RESERVES_PAYMENT_FIELDS_CONFIG = [
    {
      id: 'type_key',
      label: 'Sub-reserve',
      type: 'custom',
      customComponent: SingleSubReserveSelectFormik,
      additionalProps: {
        isSubmitting,
        showOnly,
        subReservesConfigMap,
        payableWithReserve,
      },
      disableEditAfterInitialSet: true,
      containerClassName: 'flex-1 min-w-0',
    },
    {
      id: values.request_currency && values.conversion_rate && showOnly ? 'amount_original_currency' : 'amount',
      label: 'Amount per sub-reserve',
      type: 'custom',
      customComponent: AmountFieldWithCurrencyDisplay,
      additionalProps: {
        className: classes.textField,
        fullWidth: true,
        disabled: isSubmitting,
        showOnly,
        requestCurrency: values.request_currency || requestCurrency,
        conversionRate: values.conversion_rate || conversionRate,
        policyCurrency,
      },
      containerClassName: 'flex-1 min-w-0',
    },
  ];
  return (
    <div>
      <ArrayFieldFormik
        fieldId="sub_reserve_amounts"
        innerObjectConfig={SUB_RESERVES_PAYMENT_FIELDS_CONFIG}
        addNewItemButtonText={
          shouldAllowToAddMoreSubReserve ? (
            <span className="flex items-center">
              <PlusIcon iconColor="currentColor" className="mr-4" /> Add Sub-reserve
            </span>
          ) : null
        }
        disabled={isSubmitting}
        initialNewItemValues={NEW_FIELD_INITIAL_VALUES}
      />
    </div>
  );
};

MultipleSubReservePaymentSelectFormik.propTypes = {
  payableType: PropTypes.string.isRequired,
  showOnly: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  payableWithReserve: PropTypes.object.isRequired,
  exposure: PropTypes.object,
  requestCurrency: PropTypes.string,
  conversionRate: PropTypes.number,
  policyCurrency: PropTypes.string,
};

const SubReservePaymentAmountsFormik = ({
  showOnly,
  payableType,
  exposure,
  payableWithReserve,
  isSubmitting = false,
  requestCurrency,
  conversionRate,
  policyCurrency,
}) => {
  const { isSubReservesConfigEnabled } = useExposureSubReserves(payableType, exposure);

  if (!isSubReservesConfigEnabled) {
    return <></>;
  }

  return (
    <MultipleSubReservePaymentSelectFormik
      showOnly={showOnly}
      payableType={payableType}
      exposure={exposure}
      payableWithReserve={payableWithReserve}
      isSubmitting={isSubmitting}
      requestCurrency={requestCurrency}
      conversionRate={conversionRate}
      policyCurrency={policyCurrency}
    />
  );
};

SubReservePaymentAmountsFormik.propTypes = {
  payableType: PropTypes.string.isRequired,
  showOnly: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  payableWithReserve: PropTypes.object.isRequired,
  exposure: PropTypes.object,
  requestCurrency: PropTypes.string,
  conversionRate: PropTypes.number,
  policyCurrency: PropTypes.string,
};

export { SubReservePaymentAmountsFormik, SubReservesAmountFormik, SubReservesFormik };
